import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  CalendarClock,
  CalendarRange,
  ChevronLeft,
  FileDown,
  FileText,
  ListChecks,
  Paperclip,
  Printer,
  Send,
  StickyNote,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { DayTimeline } from "@/components/DayTimeline";
import { Letterhead } from "@/components/Letterhead";
import { Markdown } from "@/components/Markdown";
import { API, api, errText } from "@/lib/api";
import { downloadWeekPack } from "@/lib/exportSheet";
import { sanitizeHtml } from "@/lib/markdown";
import { addDays, isoDate, longDate, parseISO, shortDate, startOfWeek } from "@/lib/eventMeta";
import { paperStyle } from "@/lib/postItStyles";
import { useAuth } from "@/context/AuthContext";

const Section = ({ icon: Icon, title, count, children, testId }) => (
  <section className="surface p-5 sm:p-6" data-testid={testId}>
    <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
      <Icon className="h-5 w-5" /> {title}
      {count !== undefined ? <span className="text-muted-foreground">· {count}</span> : null}
    </h2>
    <div className="mt-4">{children}</div>
  </section>
);

const KitBody = ({ kit, attachmentUrl, navigate }) => (
  <div className="space-y-5">
    <article
      className="surface p-6 sm:p-8"
      data-testid={`kit-${kit.event_id}`}
      style={{ borderLeft: `8px solid ${kit.court?.color || "rgb(var(--c-post))"}` }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="chip chip-session">{kit.when}</span>
        {kit.court?.name ? (
          <span
            className="chip"
            style={{ color: kit.court.color, borderColor: kit.court.color, background: "transparent" }}
          >
            {kit.court.name}
          </span>
        ) : null}
        {kit.urgent ? <span className="chip chip-fixed">Urgent</span> : null}
        {kit.location ? <span className="chip chip-oos">{kit.location}</span> : null}
      </div>
      <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold leading-tight">{kit.title}</h2>
      {kit.matter ? (
        <p className="mt-2 text-sm text-muted-foreground">
          {[kit.matter.case_number, kit.matter.client_name, kit.matter.stage]
            .filter(Boolean)
            .join(" · ")}
        </p>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">No matter linked to this listing.</p>
      )}
      {kit.listing_details ? (
        <p className="mt-3 text-base" data-testid="kit-listing-details">
          <span className="font-semibold">Listing: </span>
          <Markdown text={kit.listing_details} inline />
        </p>
      ) : null}
      <Markdown text={kit.notes} className="mt-4 text-base" testId="kit-notes" />
      <div className="mt-5 flex flex-wrap gap-2 print:hidden">
        {kit.vc_link ? (
          <Button
            data-testid="kit-join-btn"
            onClick={() => window.open(kit.vc_link, "_blank", "noopener")}
            className="rounded-full h-12 px-5"
          >
            <Video className="h-5 w-5 mr-2" /> Join {kit.vc_platform || "video call"}
          </Button>
        ) : null}
        {kit.matter ? (
          <Button
            variant="outline"
            data-testid="kit-open-matter-btn"
            onClick={() => navigate(`/matters/${kit.matter.matter_id}`)}
            className="rounded-full h-12 px-5"
          >
            <Briefcase className="h-5 w-5 mr-2" /> Open the matter
          </Button>
        ) : null}
      </div>
    </article>

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
      <div className="lg:col-span-7 space-y-5">
        <Section icon={FileText} title="Last order" testId="kit-last-order">
          {kit.last_log ? (
            <>
              <p className="label-caps">
                {longDate(kit.last_log.date)} · {kit.last_log.kind}
                {kit.last_log.author_name ? ` · ${kit.last_log.author_name}` : ""}
              </p>
              <div
                className="rich-text mt-3 text-base leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(kit.last_log.body_html) }}
              />
              {kit.last_log.next_date ? (
                <p className="mt-3 chip chip-session inline-flex">
                  Next date on record · {shortDate(kit.last_log.next_date)}
                </p>
              ) : null}
              {kit.last_log.attachments?.length ? (
                <div className="mt-4 space-y-2" data-testid="kit-attachments">
                  <p className="label-caps inline-flex items-center gap-1.5">
                    <Paperclip className="h-3.5 w-3.5" /> On the record
                  </p>
                  {kit.last_log.attachments.map((att) => (
                    <a
                      key={att.attachment_id}
                      href={attachmentUrl(att)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`kit-attachment-${att.attachment_id}`}
                      className="flex items-center gap-2 border border-border rounded-sm px-3 py-2.5 text-sm font-medium hover:bg-accent transition-colors duration-200"
                    >
                      <Paperclip className="h-4 w-4 shrink-0" />
                      <span className="truncate">{att.original_filename || att.filename}</span>
                    </a>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No hearing log on this matter yet.</p>
          )}
        </Section>

        {kit.history?.length ? (
          <Section
            icon={CalendarClock}
            title="Earlier hearings"
            count={kit.history_count}
            testId="kit-history"
          >
            <div className="divide-y divide-border">
              {kit.history.map((h) => (
                <div key={h.log_id} className="py-3">
                  <p className="text-sm font-semibold">
                    {shortDate(h.date)} · {h.kind}
                  </p>
                  <p className="text-sm text-muted-foreground">{h.summary}</p>
                </div>
              ))}
            </div>
          </Section>
        ) : null}
      </div>

      <div className="lg:col-span-5 space-y-5">
        <Section
          icon={FileText}
          title="Brief for the day"
          count={kit.brief_items?.length || 0}
          testId="kit-brief"
        >
          {kit.brief_items?.length ? (
            <div className="space-y-3">
              {kit.brief_items.map((item) => (
                <div key={item.item_id} className="border border-border rounded-sm p-3">
                  <Markdown text={item.instruction} className="text-sm font-semibold" />
                  {item.bare_acts ? (
                    <p className="mt-1 text-xs text-muted-foreground">Bare Acts: {item.bare_acts}</p>
                  ) : null}
                  {item.judgments ? (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Judgments: {item.judgments}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No instructions for this listing.</p>
          )}
        </Section>

        <Section
          icon={ListChecks}
          title="Open to-dos"
          count={kit.todos?.length || 0}
          testId="kit-todos"
        >
          {kit.todos?.length ? (
            <ul className="space-y-2">
              {kit.todos.map((t) => (
                <li key={t.todo_id} className="flex items-start gap-2 text-sm">
                  <span className="font-mono">[ ]</span>
                  <span>
                    <span className="font-medium">{t.title}</span>
                    <span className="block text-xs text-muted-foreground">
                      {[
                        t.due_date ? `due ${shortDate(t.due_date)}` : `since ${shortDate(t.date)}`,
                        t.priority === "high" ? "HIGH" : null,
                        t.time_hint,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Nothing open on this matter.</p>
          )}
        </Section>

        {kit.postits?.length ? (
          <Section
            icon={StickyNote}
            title="Notes on the board"
            count={kit.postits.length}
            testId="kit-postits"
          >
            <div className="space-y-3">
              {kit.postits.map((n) => (
                <div
                  key={n.post_it_id}
                  className="rounded-sm p-3"
                  style={{ ...paperStyle(n.color, "plain"), printColorAdjust: "exact" }}
                >
                  {n.title ? <p className="font-heading text-sm font-bold">{n.title}</p> : null}
                  <Markdown text={n.body} className="text-sm" />
                </div>
              ))}
            </div>
          </Section>
        ) : null}
      </div>
    </div>
  </div>
);

export default function DayKit() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const eventId = params.get("event");
  const date = params.get("date") || isoDate(new Date());
  const week = params.get("week");
  const [kits, setKits] = useState(null);
  const [index, setIndex] = useState(0);
  const [packing, setPacking] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const weekEnd = useMemo(() => (week ? isoDate(addDays(parseISO(week), 6)) : null), [week]);

  const load = useCallback(async () => {
    try {
      if (eventId) {
        const { data } = await api.get(`/kit/${eventId}`);
        setKits([data]);
      } else if (week) {
        const { data } = await api.get("/kit/range", {
          params: { start: week, end: isoDate(addDays(parseISO(week), 6)) },
        });
        setKits(data.kits);
      } else {
        const { data } = await api.get("/kit/day", { params: { date } });
        setKits(data.kits);
      }
      setIndex(0);
    } catch (e) {
      toast.error(errText(e));
      setKits([]);
    }
  }, [eventId, date, week]);

  useEffect(() => {
    load();
  }, [load]);

  const total = kits?.length || 0;
  const kit = kits?.[index];

  const step = useCallback(
    (dir) => setIndex((i) => Math.min(Math.max(i + dir, 0), Math.max(total - 1, 0))),
    [total],
  );

  useEffect(() => {
    if (week) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, week]);

  const swipe = useMemo(() => {
    let startX = null;
    return {
      onTouchStart: (e) => {
        startX = e.touches[0]?.clientX ?? null;
      },
      onTouchEnd: (e) => {
        if (week || startX === null) return;
        const dx = (e.changedTouches[0]?.clientX ?? startX) - startX;
        if (Math.abs(dx) > 70) step(dx < 0 ? 1 : -1);
        startX = null;
      },
    };
  }, [step, week]);

  const byDay = useMemo(() => {
    const groups = new Map();
    (kits || []).forEach((k) => {
      if (!groups.has(k.date)) groups.set(k.date, []);
      groups.get(k.date).push(k);
    });
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [kits]);

  const attachmentUrl = (att) =>
    `${API}/attachments/${att.attachment_id}/download` +
    (workspace?.workspace_id ? `?workspace_id=${workspace.workspace_id}` : "");

  if (kits === null) {
    return <div className="h-64 surface animate-pulse" data-testid="kit-loading" />;
  }

  return (
    <div className="space-y-6" data-testid="day-kit-page" {...swipe}>
      <style>{`@media print { .kit-day { break-after: page; } .kit-day:last-child { break-after: auto; } }`}</style>

      <Letterhead />
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <button
          data-testid="kit-back"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {eventId && kit ? (
            <Button
              variant="outline"
              data-testid="kit-whole-day-btn"
              onClick={() => setParams({ date: kit.date })}
              className="rounded-full h-11 px-5"
            >
              <CalendarClock className="h-4 w-4 mr-2" /> Whole day
            </Button>
          ) : null}
          {week ? (
            <>
              <Button
                variant="outline"
                data-testid="kit-next-week-btn"
                onClick={() => setParams({ week: isoDate(addDays(parseISO(week), 7)) })}
                className="rounded-full h-11 px-5"
              >
                <CalendarRange className="h-4 w-4 mr-2" /> Next week
              </Button>
              <Button
                data-testid="kit-pack-download-btn"
                disabled={packing}
                onClick={async () => {
                  setPacking(true);
                  try {
                    await downloadWeekPack(week);
                    toast.success("Travel pack downloaded");
                  } catch (e) {
                    toast.error(errText(e));
                  } finally {
                    setPacking(false);
                  }
                }}
                className="rounded-full h-11 px-5"
              >
                <FileDown className="h-4 w-4 mr-2" /> {packing ? "Building…" : "Travel pack PDF"}
              </Button>
              <Button
                variant="outline"
                data-testid="kit-pack-email-btn"
                disabled={emailing}
                onClick={async () => {
                  setEmailing(true);
                  try {
                    const { data } = await api.post("/kit/week/email", { start: week });
                    toast.success(`Travel pack sent to ${data.to}`, {
                      description: data.orders_merged
                        ? "The attached orders are merged into the same file."
                        : "The scans were too large to email — the notes and listings are in the pack.",
                      duration: 9000,
                    });
                  } catch (e) {
                    toast.error(errText(e));
                  } finally {
                    setEmailing(false);
                  }
                }}
                className="rounded-full h-11 px-5"
              >
                <Send className="h-4 w-4 mr-2" /> {emailing ? "Sending…" : "Email it to me"}
              </Button>
              <Button
                variant="outline"
                data-testid="kit-today-btn"
                onClick={() => setParams({ date: isoDate(new Date()) })}
                className="rounded-full h-11 px-5"
              >
                <CalendarClock className="h-4 w-4 mr-2" /> Just today
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              data-testid="kit-whole-week-btn"
              onClick={() =>
                setParams({ week: isoDate(startOfWeek(parseISO(kit?.date || date))) })
              }
              className="rounded-full h-11 px-5"
            >
              <CalendarRange className="h-4 w-4 mr-2" /> Whole week
            </Button>
          )}
          <Button
            variant="outline"
            data-testid="kit-print-btn"
            onClick={() => window.print()}
            className="rounded-full h-11 px-5"
          >
            <Printer className="h-4 w-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      <div>
        <p className="label-caps">{week ? "Week kit" : "Court day kit"}</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-2">
          {week
            ? `${shortDate(week)} — ${shortDate(weekEnd)}`
            : eventId
              ? shortDate(kit?.date || date)
              : longDate(date)}
        </h1>
        {!eventId ? (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="kit-count">
            {total} listing{total === 1 ? "" : "s"}
            {week ? " across the week — read straight through, it's all on this device." : " — step through them one by one."}
          </p>
        ) : null}
      </div>

      {total === 0 ? (
        <p
          className="text-sm text-muted-foreground border border-dashed border-border rounded-sm px-4 py-10 text-center"
          data-testid="kit-empty"
        >
          Nothing listed {week ? "this week" : "on this date"}.
        </p>
      ) : null}

      {week && total ? (
        <>
          <div className="flex flex-wrap gap-2 print:hidden" data-testid="kit-week-jump">
            {byDay.map(([day, list]) => (
              <a
                key={day}
                href={`#kit-day-${day}`}
                data-testid={`kit-jump-${day}`}
                className="chip chip-post hover:bg-accent transition-colors duration-200"
              >
                {shortDate(day)} · {list.length}
              </a>
            ))}
          </div>

          {byDay.map(([day, list]) => (
            <section key={day} id={`kit-day-${day}`} className="kit-day space-y-5 pt-4">
              <h2 className="font-heading text-2xl font-bold border-b-2 border-border pb-2">
                {longDate(day)} · {list.length} listing{list.length === 1 ? "" : "s"}
              </h2>
              <DayTimeline events={list} />
              {list.map((k) => (
                <KitBody key={k.event_id} kit={k} attachmentUrl={attachmentUrl} navigate={navigate} />
              ))}
            </section>
          ))}
        </>
      ) : null}

      {!week && kit ? (
        <>
          {kits.length ? <DayTimeline events={kits} /> : null}
          {total > 1 ? (
            <div
              className="flex items-center justify-between gap-3 print:hidden"
              data-testid="kit-stepper"
            >
              <Button
                variant="outline"
                data-testid="kit-prev"
                disabled={index === 0}
                onClick={() => step(-1)}
                className="rounded-full h-14 px-6 text-base"
              >
                <ArrowLeft className="h-5 w-5 mr-2" /> Previous
              </Button>
              <span className="text-sm font-semibold tabular-nums" data-testid="kit-position">
                {index + 1} of {total}
              </span>
              <Button
                data-testid="kit-next"
                disabled={index >= total - 1}
                onClick={() => step(1)}
                className="rounded-full h-14 px-6 text-base"
              >
                Next <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          ) : null}

          <KitBody kit={kit} attachmentUrl={attachmentUrl} navigate={navigate} />
        </>
      ) : null}
    </div>
  );
}
